import { useMemo } from 'react';

import { makeStyles, Paper, Typography } from '@material-ui/core';
import { Info } from '@material-ui/icons';

import { useFeatureFlags } from 'hooks/useFeatureFlags';
import {
  AtendimentoInterface,
  FeatureFlagsEnum,
  StatusSessaoEnum,
} from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import { ContatoInterface } from 'store/reducers/contatos';
import { MensagemInterface } from 'store/reducers/mensagens';
import { TemplateInterface } from 'store/reducers/templates';

import ButtonSendTagMessage from './ButtonSendTagMessage';
import ButtonSendTemplateMessage from './ButtonSendTemplateMessage';
// Importação para Rollback rápido (input original usando TextField):
// import InputFormMessage from './InputFormMessage';
import InputFormMessage from './InputFormMessageV2';

const useStyles = makeStyles(({ spacing }) => ({
  paper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBlock: spacing(1),
    padding: spacing(1.5),
    gap: spacing(1),
  },
}));

interface ConditionalInputMessageProps {
  atendimento: AtendimentoInterface;
  replyMessage: MensagemInterface | null;
  onSendTemplate: (template: TemplateInterface) => void;
  onSendText: (value: string, callback: (isSuccess: boolean) => void) => void;
  onSendInstagramTagMessage: (
    value: string,
    callback: (isSuccess: boolean) => void,
  ) => void;
  onSendContato: (contato: ContatoInterface) => void;
  onSendAudio: (mediaBlob: Blob, duration: number) => void;
  onSendFilesFromGaleria: (storageIds: string[]) => void;
  onSendCTA: () => void;
  onSelectFiles?: (files: File[]) => void;
}

export default function ConditionalInputMessage({
  atendimento,
  replyMessage,
  onSendTemplate,
  onSendText,
  onSendInstagramTagMessage,
  onSendContato,
  onSendAudio,
  onSendFilesFromGaleria,
  onSendCTA,
  onSelectFiles,
}: ConditionalInputMessageProps) {
  const classes = useStyles();

  const { hasFeature } = useFeatureFlags();

  const isInstagramSendMessageTagged = useMemo(
    () => hasFeature(FeatureFlagsEnum.INSTAGRAM_SEND_MESSAGE_TAGGED),
    [hasFeature],
  );

  const {
    sessaoStatus,
    sessaoTaggedStatus,
    canal: { tipoCanal },
  } = atendimento;

  const closedSession = sessaoStatus === StatusSessaoEnum.CLOSED;
  const isInstagramOrMessengerChannel =
    tipoCanal === TipoCanalEnum.INSTAGRAM ||
    tipoCanal === TipoCanalEnum.MESSENGER;

  const isOfficialWhatsAppChannel =
    tipoCanal === TipoCanalEnum.WHATSAPP_OFICIAL ||
    (atendimento?.canal?.isModoHibrido &&
      atendimento?.canal?.modoEnvio === 'API_OFICIAL');

  const isBlocked = !!(
    atendimento.contato.blockedAt || atendimento.contato?.bloqueadoEm
  );

  if (isBlocked) {
    return (
      <Paper className={classes.paper} elevation={0}>
        <Info color="error" />
        <Typography variant="body2" color="error">
          Não é possível enviar mensagens porque o contato está bloqueado
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {closedSession && isOfficialWhatsAppChannel && (
        <ButtonSendTemplateMessage onSendTemplate={onSendTemplate} />
      )}

      {closedSession && isInstagramOrMessengerChannel && (
        <>
          {isInstagramSendMessageTagged && sessaoTaggedStatus ? (
            <ButtonSendTagMessage
              onSendInstagramTagMessage={onSendInstagramTagMessage}
            />
          ) : (
            <Paper className={classes.paper} elevation={0}>
              <Info color="primary" />
              <Typography variant="body2">
                Não é possível enviar mensagens porque a sessão está fechada
              </Typography>
            </Paper>
          )}
        </>
      )}

      {!closedSession && (
        <InputFormMessage
          onSelectFiles={onSelectFiles}
          onSendInputText={onSendText}
          onSendContato={onSendContato}
          onSendAudio={onSendAudio}
          // onSendImagem={handleSendImagem}
          // onSendDocument={handleSendDocumento}
          onSendFromGaleria={replyMessage ? undefined : onSendFilesFromGaleria}
          onSendCTA={replyMessage ? undefined : onSendCTA}
        />
      )}
    </>
  );
}
